import { useState, useEffect, useMemo } from 'react';
import styles from './GerenciamentoPedidos.module.css';
import SideBarGestaoAdmin from '../components/SideBarGestaoAdmin';
import MobileMenu from '../../../shared/components/MobileMenu';
import HeaderAdmin from '../components/HeaderAdmin';
import PedidoTabelaAdmin from '../components/PedidoTabelaAdmin';
import { BiMessageSquareX } from 'react-icons/bi';
import { MdOutlineAttachMoney, MdShoppingBag, MdAccessTime, MdKeyboardArrowDown } from 'react-icons/md';
import { sseService } from '../../../shared/services/sse';
import { numeroExibicaoPedido } from '../../../shared/utils/pedido';

function GerenciamentoPedidos() {
    const [filtroStatus, setFiltroStatus] = useState('todos');
    const [buscaPedido, setBuscaPedido] = useState('');
    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [valorMin, setValorMin] = useState('');
    const [valorMax, setValorMax] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [expandedCard, setExpandedCard] = useState(null);

    const buscarPedidos = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('token');
            const estabelecimentoId = localStorage.getItem('estabelecimento_id');
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const [responsePedidos, responseItens, responseProdutos] = await Promise.all([
                fetch(`${API_URL}/buscapedidos?estabelecimento_id=${estabelecimentoId}`, { headers }),
                fetch(`${API_URL}/buscaitenspedido?estabelecimento_id=${estabelecimentoId}`, { headers }),
                fetch(`${API_URL}/cadastroprodutos?id_estabelecimento=${estabelecimentoId}`, { headers }),
            ]);

            if (!responsePedidos.ok) throw new Error(responsePedidos.statusText);
            if (!responseItens.ok) throw new Error(responseItens.statusText);
            if (!responseProdutos.ok) throw new Error(responseProdutos.statusText);

            const [dataPedidos, dataItens, dataProdutos] = await Promise.all([
                responsePedidos.json(),
                responseItens.json(),
                responseProdutos.json(),
            ]);

            const produtosPorId = new Map(dataProdutos.map(p => [String(p.id_item), p]));
            const itensPorPedido = new Map();
            for (const item of dataItens) {
                const chave = String(item.id_pedido);
                if (!itensPorPedido.has(chave)) itensPorPedido.set(chave, []);
                const produto = produtosPorId.get(String(item.id_produto));
                itensPorPedido.get(chave).push({
                    ...item,
                    nome_produto: produto ? produto.nome_item : (item.nome_item_externo || `Produto #${item.id_produto}`),
                    preco: produto ? produto.preco : (item.total_item / item.quantidade)
                });
            }

            const pedidosComItens = dataPedidos.map(pedido => ({
                ...pedido,
                itens: itensPorPedido.get(String(pedido.id_pedido)) || []
            }));

            setPedidos(pedidosComItens);
        } catch (error) {
            console.log("Erro ao buscar pedidos:", error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        buscarPedidos();

        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }

        const estabelecimentoId = localStorage.getItem('estabelecimento_id');
        if (estabelecimentoId) sseService.connect(estabelecimentoId);

        const handleNovoPedido = (data) => {
            buscarPedidos();
            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('Novo Pedido Recebido!', {
                    body: `Pedido #${numeroExibicaoPedido(data.pedido)} - Total: R$ ${data.pedido.total}`,
                    icon: '/logo192.png',
                    tag: `pedido-${data.pedido.id_pedido}`,
                    requireInteraction: true
                });
            }
        };

        const handlePedidoAtualizado = (data) => {
            setPedidos(prev =>
                prev.map(pedido =>
                    pedido.id_pedido === data.pedido.id_pedido
                        ? { ...pedido, ...data.pedido }
                        : pedido
                )
            );
        };

        sseService.on('novo_pedido', handleNovoPedido);
        sseService.on('pedido_atualizado', handlePedidoAtualizado);

        return () => {
            sseService.off('novo_pedido', handleNovoPedido);
            sseService.off('pedido_atualizado', handlePedidoAtualizado);
        };
    }, []);

    const pedidosFiltrados = useMemo(() => {
        const estabelecimentoId = localStorage.getItem('estabelecimento_id');
        let resultado = pedidos.filter(p =>
            String(p.id_estabelecimento) === String(estabelecimentoId)
        );
        if (buscaPedido.trim()) resultado = resultado.filter(p => String(numeroExibicaoPedido(p)).includes(buscaPedido.trim()));
        if (filtroStatus !== 'todos') resultado = resultado.filter(p => p.status === filtroStatus);
        if (valorMin !== '') resultado = resultado.filter(p => Number(p.total) >= Number(valorMin));
        if (valorMax !== '') resultado = resultado.filter(p => Number(p.total) <= Number(valorMax));
        if (dataInicio !== '') resultado = resultado.filter(p => new Date(p.data_pedido) >= new Date(dataInicio));
        if (dataFim !== '') {
            resultado = resultado.filter(p => {
                const fim = new Date(dataFim);
                fim.setHours(23, 59, 59, 999);
                return new Date(p.data_pedido) <= fim;
            });
        }
        return resultado;
    }, [pedidos, filtroStatus, buscaPedido, valorMin, valorMax, dataInicio, dataFim]);

    const kpis = useMemo(() => {
        const faturamento = pedidosFiltrados
            .filter(p => p.status === 'entregue' && !p.is_teste) // pedido de teste (iFood) não conta no faturamento
            .reduce((total, p) => total + Number(p.total || 0), 0);
        const quantidadePedidos = pedidosFiltrados.length;
        const pedidosEntregues = pedidosFiltrados.filter(p => p.status === 'entregue' && p.data_pedido && p.data_entrega);
        let tempoMedio = 0;
        if (pedidosEntregues.length > 0) {
            const soma = pedidosEntregues.reduce((total, p) => {
                return total + (new Date(p.data_entrega) - new Date(p.data_pedido)) / 1000 / 60;
            }, 0);
            tempoMedio = Math.round(soma / pedidosEntregues.length);
        }
        const pedidosEmAndamento = pedidosFiltrados.filter(p =>
            p.status === 'pendente' || p.status === 'em andamento'
        ).length;
        return { faturamento, quantidadePedidos, tempoMedio, pedidosEmAndamento };
    }, [pedidosFiltrados]);

    const getBadgeClass = (status) => {
        const map = {
            'pendente': styles.badge_pendente,
            'em andamento': styles.badge_em_andamento,
            'pronto': styles.badge_pronto,
            'entregue': styles.badge_entregue,
            'cancelado': styles.badge_cancelado,
        };
        return map[status] || '';
    };

    const formatarTempo = (dataPedido) => {
        const diff = (Date.now() - new Date(dataPedido)) / 1000 / 60;
        if (diff < 60) return `Há ${Math.round(diff)} min`;
        return `Há ${Math.round(diff / 60)}h ${Math.round(diff % 60)}min`;
    };

    return (
        <div className={styles.pageGestao}>
            <div className={styles.wrapperDashboard}>
                <MobileMenu />
                <SideBarGestaoAdmin />

                <div className={styles.containerDashboard}>

                    {/* Header — aparece sempre */}
                    <HeaderAdmin />

                    <div className={styles.contentWrapper}>

                        {/* KPIs */}
                        <div className={styles.kpisContainer}>
                            <div className={styles.kpiCard}>
                                <div className={styles.kpiHead}>
                                    <p className={styles.kpiLabel}>Faturamento</p>
                                    <MdOutlineAttachMoney className={styles.kpiIcon} />
                                </div>
                                <h3 className={styles.kpiValue}>
                                    R$ {kpis.faturamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </h3>
                            </div>

                            <div className={styles.kpiCard}>
                                <div className={styles.kpiHead}>
                                    <p className={styles.kpiLabel}>Pedidos</p>
                                    <MdShoppingBag className={styles.kpiIcon} />
                                </div>
                                <h3 className={styles.kpiValue}>{kpis.quantidadePedidos}</h3>
                            </div>

                            <div className={styles.kpiCard}>
                                <div className={styles.kpiHead}>
                                    <p className={styles.kpiLabel}>Tempo Médio</p>
                                    <MdAccessTime className={styles.kpiIcon} />
                                </div>
                                <h3 className={styles.kpiValue}>{kpis.tempoMedio} min</h3>
                            </div>
                        </div>


                         <div className={styles.searchPedidoContainer}>
                                    <input
                                        type="text"
                                        placeholder="Buscar por nº do pedido..."
                                        value={buscaPedido}
                                        onChange={(e) => setBuscaPedido(e.target.value)}
                                        className={styles.searchPedidoInput}
                                    />
                                </div>
                        {/* Filtros */}
                        <div className={styles.filtersOuterContainer}>
                            <div className={styles.filtersWrapper}>
                             

                                
                        

                                <div className={styles.filterContainer}>
                                    {['todos', 'pendente', 'em andamento', 'pronto', 'entregue'].map(status => (
                                        <button
                                            key={status}
                                            className={`${styles.filterTab} ${filtroStatus === status ? styles.active : ''}`}
                                            onClick={() => setFiltroStatus(status)}
                                        >
                                            {{ 'todos': 'Todos', 'pendente': 'Pendentes', 'em andamento': 'Em Andamento', 'pronto': 'Pronto', 'entregue': 'Entregue' }[status]}
                                        </button>
                                    ))}
                                </div>

                            </div>
                        </div>

                        {/* Conteúdo */}
                        <div className={styles.contentDashboard}>

                            {/* Tabela — só desktop */}
                            <div className={styles.tabelaPedidos}>
                                {loading ? (
                                    <p className={styles.loadingMsg}>Carregando pedidos...</p>
                                ) : pedidosFiltrados.length > 0 ? (
                                    <PedidoTabelaAdmin pedidos={pedidosFiltrados} />
                                ) : (
                                    <div className={styles.emptyMessage}>
                                        <p>Não há pedidos com o filtro aplicado.</p>
                                    </div>
                                )}
                            </div>

                            {/* Cards — só mobile */}
                            <div className={styles.cardsPedidos}>
                                {loading ? (
                                    <p className={styles.loadingMobile}>Carregando pedidos...</p>
                                ) : pedidosFiltrados.length > 0 ? (
                                    pedidosFiltrados.map(pedido => {
                                        const isOpen = expandedCard === pedido.id_pedido;
                                        const qtdItens = pedido.itens?.length || 0;
                                        return (
                                            <div key={pedido.id_pedido} className={styles.pedidoCard}>

                                                {/* Header clicável */}
                                                <div
                                                    className={styles.pedidoCardHeader}
                                                    onClick={() => setExpandedCard(isOpen ? null : pedido.id_pedido)}
                                                >
                                                    <div className={styles.pedidoHeaderMain}>
                                                        <span className={styles.pedidoNum}>#{numeroExibicaoPedido(pedido)}</span>
                                                        <span className={`${styles.pedidoBadge} ${getBadgeClass(pedido.status)}`}>
                                                            {pedido.status}
                                                        </span>
                                                    </div>
                                                    <div className={styles.pedidoHeaderRight}>
                                                        <span className={styles.pedidoTempo}>
                                                            <MdAccessTime size={13} /> {formatarTempo(pedido.data_pedido)}
                                                        </span>
                                                        <MdKeyboardArrowDown
                                                            size={20}
                                                            className={`${styles.pedidoChevron} ${isOpen ? styles.pedidoChevronOpen : ''}`}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Info: cliente + resumo */}
                                                <div className={styles.pedidoInfo}>
                                                    {pedido.nome_cliente && (
                                                        <span className={styles.pedidoCliente}>👤 {pedido.nome_cliente}</span>
                                                    )}
                                                    <span className={styles.pedidoResumo}>
                                                        {qtdItens} {qtdItens === 1 ? 'item' : 'itens'} · R$ {Number(pedido.total).toFixed(2)}
                                                    </span>
                                                </div>

                                                {/* Itens — expansível */}
                                                {isOpen && (
                                                    <div className={styles.pedidoItensLista}>
                                                        {pedido.itens?.map((item, i) => (
                                                            <div key={i} className={styles.pedidoItem}>
                                                                <span className={styles.pedidoItemNome}>{item.quantidade}x {item.nome_produto}</span>
                                                                <span className={styles.pedidoItemVal}>R$ {Number(item.total_item).toFixed(2)}</span>
                                                            </div>
                                                        ))}
                                                        <div className={styles.pedidoTotal}>
                                                            <span className={styles.pedidoTotalLabel}>Total</span>
                                                            <span className={styles.pedidoTotalVal}>R$ {Number(pedido.total).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className={styles.emptyMessage}>
                                        <BiMessageSquareX size={40} color="#e74c3c" />
                                        <p>Nenhum pedido encontrado.</p>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GerenciamentoPedidos;